import {
  Card,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Alert,
  Box,
  Icon,
  Divider
} from "@mui/material";
import WDBox from "../../../../MaterialComponents/WDBox";
import WDButton from "../../../../MaterialComponents/WDButton";
import WDTypography from "../../../../MaterialComponents/WDTypography";
import AppService from "../../../../Configurations/Appservice";

import { Formik, Form, Field } from "formik";
import { object, string } from "yup";
import { useEffect, useState } from "react";
import { CUSTOMER_ORG_TYPE } from "../../../../Configurations/AppConfig";
import { useDispatch } from "react-redux";
import {
  dismissLoader,
  showLoader,
  showSuccessMessage,
  updateScreenTitle,
} from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import SubTitle from "../../../../Components/SubTitle";

const initialValues = {
  name: "",
  tradeLicenseNumber: "",
  contactPerson: "",
  email: "",
  contactNumber: "",
  address: "",
  organizationCategory: "",
  businessCategoryId: "",
};

const validationSchema = object({
  name: string().required("Company Name is Mandatory"),
  tradeLicenseNumber: string(),
  contactPerson: string().required("Contact Person is Mandatory"),
  email: string().email("Invalid email format").required("Email is Mandatory"),
  contactNumber: string().required("Contact Number is Mandatory"),
  address: string().required("Address is Mandatory"),
  organizationCategory: string().required("Organization Category is Mandatory"),
  businessCategoryId: string().required("Business Category is Mandatory"),
});

const NewCustomerOrganisations = () => {
  const dispatch = useDispatch();
  const [businessCategories, setBusinessCategories] = useState([]);
  const [organizationCategories, setOrganizationCategories] = useState([]);

  useEffect(() => {
    dispatch(updateScreenTitle("New Organisation"));
    
    // Load organization categories from API
    AppService.get("api/organisations/categories")
      .then((response) => {
        setOrganizationCategories(response || []);
      })
      .catch((error) => {
        console.error("Failed to load organization categories", error);
      });
    
    // Load business categories from API
    AppService.get("api/business-categories/list-details")
      .then((response) => {
        setBusinessCategories(response || []);
      })
      .catch((error) => {
        console.error("Failed to load business categories", error);
      });
    
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const _onSubmit = (values, { resetForm }) => {
    dispatch(showLoader());
    values.type = CUSTOMER_ORG_TYPE;
    if (!values.businessCategoryId) {
      values.businessCategoryId = null;
    }
    if (!values.organizationCategory) {
      values.organizationCategory = null;
    }
    AppService.post("v1/organisation", values)
      .then((response) => {
        resetForm();
        dispatch(showSuccessMessage("New Customer Organisation Created Successfully"));
      })
      .catch((error) => {})
      .finally(() => {
        dispatch(dismissLoader());
      });
  };

  const getSelectedCategoryInfo = (categoryId) => {
    const category = businessCategories.find(c => c.id === categoryId);
    if (!category) return null;
    return category;
  };

  return (
    <WDBox>
      <Card sx={{ padding: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={_onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, isValid, touched, dirty, values, setFieldValue }) => (
            <Form>
              {/* Section: Company Information */}
              <WDBox mb={2}>
                <SubTitle fontSize={14}>Company Information</SubTitle>
              </WDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Field
                    name="name"
                    label="Company Name *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.name) && Boolean(touched.name)}
                    helperText={Boolean(touched.name) && errors.name}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="tradeLicenseNumber"
                    label="Trade License Number"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.tradeLicenseNumber) && Boolean(touched.tradeLicenseNumber)}
                    helperText={Boolean(touched.tradeLicenseNumber) && errors.tradeLicenseNumber}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.organizationCategory) && Boolean(touched.organizationCategory)}
                  >
                    <InputLabel>Organization Category *</InputLabel>
                    <Select
                      name="organizationCategory"
                      value={values.organizationCategory}
                      label="Organization Category *"
                      onChange={(e) => setFieldValue("organizationCategory", e.target.value)}
                    >
                      {organizationCategories.map((type) => (
                        <MenuItem key={type.code} value={type.code}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(touched.organizationCategory) && errors.organizationCategory && (
                      <FormHelperText>{errors.organizationCategory}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    error={Boolean(errors.businessCategoryId) && Boolean(touched.businessCategoryId)}
                  >
                    <InputLabel>Business Category *</InputLabel>
                    <Select
                      name="businessCategoryId"
                      value={values.businessCategoryId}
                      label="Business Category *"
                      onChange={(e) => setFieldValue("businessCategoryId", e.target.value)}
                    >
                      {businessCategories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Icon sx={{ color: category.colour, fontSize: 20 }}>{category.icon}</Icon>
                            <span>{category.name}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                    {Boolean(touched.businessCategoryId) && errors.businessCategoryId && (
                      <FormHelperText>{errors.businessCategoryId}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    name="address"
                    label="Address *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.address) && Boolean(touched.address)}
                    helperText={Boolean(touched.address) && errors.address}
                  />
                </Grid>

                {/* Business Category Info Alert */}
                {values.businessCategoryId && (
                  <Grid item xs={12}>
                    {(() => {
                      const selectedCategory = getSelectedCategoryInfo(values.businessCategoryId);
                      if (!selectedCategory) return null;
                      const requiredDocs = selectedCategory.documents || [];
                      const mandatoryDocs = requiredDocs.filter(d => d.isMandatory);

                      return (
                        <Alert
                          severity="info"
                          icon={<Icon>{selectedCategory.icon}</Icon>}
                          sx={{
                            backgroundColor: `${selectedCategory.colour}15`,
                            '& .MuiAlert-icon': { color: selectedCategory.colour }
                          }}
                        >
                          <WDTypography variant="body2" fontWeight="medium">
                            {selectedCategory.name}
                          </WDTypography>
                          <WDTypography variant="caption" color="text.secondary">
                            {selectedCategory.description}
                          </WDTypography>
                          {mandatoryDocs.length > 0 && (
                            <WDTypography variant="caption" display="block" mt={1}>
                              <strong>Industry-specific documents required:</strong>{" "}
                              {mandatoryDocs.map(d => d.documentTypeName || d.documentTypeCode.replace(/_/g, ' ')).join(', ')}
                            </WDTypography>
                          )}
                        </Alert>
                      );
                    })()}
                  </Grid>
                )}
              </Grid>

              {/* Section: Contact Details */}
              <Divider sx={{ my: 3 }} />
              <WDBox mb={2}>
                <SubTitle fontSize={14}>Contact Details</SubTitle>
              </WDBox>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Field
                    name="contactPerson"
                    label="Contact Person *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.contactPerson) && Boolean(touched.contactPerson)}
                    helperText={Boolean(touched.contactPerson) && errors.contactPerson}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="email"
                    type="email"
                    label="Email *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.email) && Boolean(touched.email)}
                    helperText={Boolean(touched.email) && errors.email}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    name="contactNumber"
                    label="Contact Number *"
                    fullWidth
                    as={TextField}
                    error={Boolean(errors.contactNumber) && Boolean(touched.contactNumber)}
                    helperText={Boolean(touched.contactNumber) && errors.contactNumber}
                  />
                </Grid>
              </Grid>

              <WDBox display="flex" justifyContent="flex-end" pt={3}>
                <WDButton variant="gradient" disabled={!dirty || !isValid} type="submit">
                  Create Organisation
                </WDButton>
              </WDBox>
            </Form>
          )}
        </Formik>
      </Card>
    </WDBox>
  );
};

export default NewCustomerOrganisations;
